// ---------------------------------------------------------------------------
// ozel-alan.types.ts
//
// "Özel Alan" (ek.ozelAlanlar[]) şeması ve yardımcıları.
// ek.parametreler[] (sistem parametre tanımları) İLE KARIŞTIRILMAZ.
//
// Geriye dönük uyumluluk: mevcut kayıtlarda `tip` alanı olmayabilir
// (eski veri: sadece {ad, deger}). Böyle durumda tip 'metin' varsayılır —
// ozelAlanTipiCoz() bunu garanti eder, hiçbir yerde a.tip doğrudan okunmaz.
// ---------------------------------------------------------------------------

export type OzelAlanTipi =
  | 'metin'
  | 'coklu-metin'
  | 'sayi'
  | 'tarih'
  | 'onay'
  | 'secim'
  | 'renk';

export interface IOzelAlan {
  ad: string;
  deger: any;
  /** Yoksa 'metin' varsayılır (eski kayıtlarla uyumluluk). */
  tip?: OzelAlanTipi;
  /** Sadece tip:'secim' için — seçenek listesi. */
  secenekler?: string[];
}

export interface IOzelAlanTipBilgi {
  etiket: string;
  ikon: string;
}

export const OZEL_ALAN_TIP_BILGI: Record<OzelAlanTipi, IOzelAlanTipBilgi> = {
  metin:        { etiket: 'Metin',       ikon: 'font-size' },
  'coklu-metin':{ etiket: 'Uzun Metin',  ikon: 'align-left' },
  sayi:         { etiket: 'Sayı',        ikon: 'number' },
  tarih:        { etiket: 'Tarih',       ikon: 'calendar' },
  onay:         { etiket: 'Evet / Hayır',ikon: 'check-square' },
  secim:        { etiket: 'Seçim Listesi', ikon: 'unordered-list' },
  renk:         { etiket: 'Renk',        ikon: 'bg-colors' },
};

export const OZEL_ALAN_TIPLERI: OzelAlanTipi[] =
  Object.keys(OZEL_ALAN_TIP_BILGI) as OzelAlanTipi[];

/** a.tip her zaman güvenle bu fonksiyon üzerinden okunmalı. */
export function ozelAlanTipiCoz(a: Partial<IOzelAlan> | null | undefined): OzelAlanTipi {
  const t = a?.tip;
  return (t && OZEL_ALAN_TIP_BILGI[t]) ? t : 'metin';
}

/** Bir alanın tipi değiştiğinde değeri o tipe uygun makul bir başlangıca çevirir. */
export function ozelAlanDegeriDonustur(deger: any, yeniTip: OzelAlanTipi): any {
  switch (yeniTip) {
    case 'sayi': {
      const n = +deger;
      return Number.isFinite(n) ? n : 0;
    }
    case 'onay':
      return !!deger && deger !== '0' && deger !== 'false';
    case 'tarih': {
      if (!deger) return null;
      const d = new Date(deger);
      return isNaN(d.getTime()) ? null : d.toISOString();
    }
    case 'secim':
      return deger != null ? String(deger) : '';
    default:
      return deger != null ? deger : '';
  }
}

/** Salt-okunur (kart/görüntüleme) bağlamda kullanıcıya gösterilecek metin. */
export function ozelAlanGorunenDeger(a: IOzelAlan): string {
  const tip = ozelAlanTipiCoz(a);
  switch (tip) {
    case 'onay':
      return a.deger ? 'Evet' : 'Hayır';
    case 'tarih': {
      if (!a.deger) return '—';
      const d = new Date(a.deger);
      return isNaN(d.getTime()) ? String(a.deger) : d.toLocaleDateString('tr-TR');
    }
    case 'sayi':
      return a.deger != null && a.deger !== '' ? String(a.deger) : '0';
    default:
      return a.deger != null && a.deger !== '' ? String(a.deger) : '—';
  }
}

/** Yeni boş bir özel alan oluşturur. */
export function ozelAlanOlustur(tip: OzelAlanTipi = 'metin'): IOzelAlan {
  return {
    ad: '',
    deger: ozelAlanDegeriDonustur(undefined, tip),
    tip,
    ...(tip === 'secim' ? { secenekler: [] } : {}),
  };
}
